import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faLocationArrow } from "@fortawesome/pro-solid-svg-icons/faLocationArrow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EventApi } from "@fullcalendar/react";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isSameDay } from "date-fns/esm";
import { useIntl } from "react-intl";

import ActivityStatus from "@mapmycustomers/shared/enum/activity/ActivityStatus";
import { Activity } from "@mapmycustomers/shared/types/entity";

import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import EventObject from "@app/scene/activity/component/ActivityCalendarPage/type/EventObject";
import useCalendarContext from "@app/scene/activity/component/ActivityCalendarPage/utils/useCalendarContext";
import colors from "@app/styles/_colors.module.scss";
import getActivityStatus from "@app/util/activity/getActivityStatus";
import getBadgeImage from "@app/util/badge/getBadgeImage";
import { getBadgeSvgWithColor } from "@app/util/badge/getSvgForBadge";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import AssociatedRecordLink from "./AssociatedRecordLink";
import styles from "./DayViewEvent.module.scss";

interface Props {
  activity: Activity;
  event: EventApi;
  eventObject: EventObject;
}

const DayViewEvent: React.FC<Props> = ({ activity, event, eventObject }) => {
  const intl = useIntl();
  const isDetailedView =
    (!event.allDay && activity.duration && activity.duration > 51) ||
    (!activity.endAt && !activity.emailLogId);

  const smallCard =
    !((!event.allDay && activity.duration && activity.duration > 16) || !activity.endAt) ||
    !!activity.emailLogId;

  const informationElement = useRef<HTMLDivElement | null>(null);
  const [contentRowsCount, setContentRowsCount] = useState<number>(1);

  useEffect(() => {
    // TODO 14 is font size for event card. We need to move this to scss file.
    setContentRowsCount(Math.floor((informationElement.current?.clientHeight ?? 0) / 14));
  }, [activity, setContentRowsCount]);

  const isMultiDayActivity =
    activity.endAt &&
    activity.startAt &&
    !isSameDay(parseApiDate(activity.startAt!), parseApiDate(activity.endAt!));
  const dateFormat = isMultiDayActivity ? "ccc p" : "p";

  const { onNearByClick } = useCalendarContext();

  const handleNearByClick = useCallback(
    (e) => {
      e.stopPropagation();
      onNearByClick?.(eventObject);
    },
    [eventObject, onNearByClick]
  );

  const isActivityOverdue = getActivityStatus(activity) === ActivityStatus.OVERDUE;

  const activityTime = useMemo(
    () => (
      <span>
        {formatDate(event.start ?? parseApiDate(activity.startAt!), dateFormat)}
        {isMultiDayActivity ? ` - ${formatDate(parseApiDate(activity.endAt!), dateFormat)}` : ""}
      </span>
    ),
    [activity, dateFormat, event, isMultiDayActivity]
  );

  const associatedEntity = useMemo(
    () => <AssociatedRecordLink activity={activity} className={styles.associatedEntity} />,
    [activity]
  );

  return (
    <div className={styles.container}>
      <div
        className={cn(styles.content, {
          [styles.contentWithPadding]: !smallCard,
        })}
      >
        <div className={styles.titleWithIcon}>
          {activity.completed && (
            <div className={styles.completedIconContainer}>
              <FontAwesomeIcon className={styles.completedIcon} icon={faCheck} />
            </div>
          )}
          <div
            className={cn(styles.title, {
              [styles.titleInline]: activity.completed,
            })}
          >
            {activity.name}
            {(!isDetailedView || contentRowsCount === 0) && (
              <>
                {activity.allDay ? null : <>&nbsp;&#183; {activityTime}</>}
                {activity.allDay && (activity.account || activity.contact || activity.deal) ? (
                  <>&nbsp;&#183;</>
                ) : null}
                {associatedEntity}
              </>
            )}
          </div>
        </div>
        {isDetailedView && contentRowsCount > 0 && (
          <div className={styles.information} ref={informationElement}>
            <LongContentParagraph
              className={styles.description}
              expandable={false}
              noMargin
              rows={contentRowsCount}
            >
              {activityTime}
              {associatedEntity}
            </LongContentParagraph>
          </div>
        )}
      </div>
      {isActivityOverdue && (
        <div
          className={cn(styles.overdueContainer, {
            [styles.overdueContainerForSmallEvent]: smallCard,
          })}
        >
          {getBadgeImage(getBadgeSvgWithColor(colors.red100), 20)}
        </div>
      )}
      {eventObject.suggestion && (
        <div className={styles.nearBy} onClickCapture={handleNearByClick}>
          <Tooltip
            title={intl.formatMessage(
              {
                id: "activity.calendarView.calendar.suggestion.nearby.title",
                defaultMessage:
                  "{count} {count, plural, one {nearby customer} other {nearby customers}}",
                description: "Title of the NearBy smart suggestion in Activities Calendar",
              },
              {
                count: eventObject.suggestion.count ?? 0,
              }
            )}
          >
            <FontAwesomeIcon icon={faLocationArrow} />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default DayViewEvent;
